import { supabase } from '@/integrations/supabase/client';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { formatDateToString } from '@/lib/dateUtils';
import {
  directPathError,
  getStaffSessionToken,
  handleInvalidSession,
  isInvalidSessionError,
} from '@/services/staffSession';

// `completed` is LEGACY. Samuel, 29 Sep 2026: "the status of Completed and
// Referred Back should be the same." The one status is `referred_back`.
// normalizePatientCaseStatus() maps `completed` to it before the app saves or
// shows a case, and the patient_cases trigger (20260929120000) does the same
// for every other writer. It stays in this list only because the database
// CHECK still allows it, so an old row or a stale writer never errors.
export const PATIENT_CASE_STATUS_OPTIONS = [
  'new_referral',
  'appointment_scheduled',
  'patient_seen',
  'treatment_proposed',
  'treatment_accepted',
  'treatment_ongoing',
  'referred_to_other_specialty',
  'no_show',
  'non_compliant',
  'patient_did_not_proceed',
  'lost_inactive',
  'completed',
  'referred_back',
] as const;

export const PATIENT_CASE_ACTIVITY_TYPES = [
  'status_change',
  'communication',
  'ownership_change',
  'note',
  'referred_back',
] as const;

export type PatientCaseStatus = (typeof PATIENT_CASE_STATUS_OPTIONS)[number];
export type PatientCaseActivityType = (typeof PATIENT_CASE_ACTIVITY_TYPES)[number];
export type PatientCaseConversionStatus = 'pending' | 'converted' | 'not_converted' | 'inactive';

export type PatientCase = Tables<'patient_cases'>;
export type PatientCaseInsert = TablesInsert<'patient_cases'>;
export type PatientCaseUpdate = TablesUpdate<'patient_cases'>;
export type PatientCaseActivity = Tables<'patient_case_activities'>;
export type PatientCaseActivityInsert = TablesInsert<'patient_case_activities'>;

type ClinicSummary = Pick<Tables<'clinics'>, 'id' | 'name' | 'code'>;
type StaffSummary = Pick<Tables<'staff'>, 'id' | 'name' | 'short_name' | 'role' | 'color'>;
type UserSummary = Pick<Tables<'users'>, 'id' | 'name' | 'email'>;

export interface PatientCaseWithDetails extends PatientCase {
  clinic?: ClinicSummary | null;
  assigned_owner?: StaffSummary | null;
  created_by_user?: UserSummary | null;
}

export interface PatientCaseActivityWithDetails extends PatientCaseActivity {
  created_by_user?: UserSummary | null;
}

export interface PatientCaseFilters {
  clinicId?: string;
  status?: PatientCaseStatus | 'all';
  referringDoctor?: string;
  referringClinic?: string;
  assignedOwnerStaffId?: string;
  search?: string;
  includeClosed?: boolean;
}

const patientCaseSelect = `
  *,
  clinic:clinics(id, name, code),
  assigned_owner:staff(id, name, short_name, role, color),
  created_by_user:users!patient_cases_created_by_fkey(id, name, email)
`;

const patientCaseActivitySelect = `
  *,
  created_by_user:users(id, name, email)
`;

// ---------------------------------------------------------------------------
// How patient_cases is reached
// ---------------------------------------------------------------------------
// This table used to be queried straight from the browser with the publishable
// key, which ships inside the JS bundle app.twincityendo.com.sg serves to every
// visitor — so all 1,048 records, names on all of them and NRIC and phone on
// 24, were readable by anyone who looked. Every operation below now goes
// through a *_by_session SECURITY DEFINER function (migration 20260930110000)
// carrying the staff session token the server issued at sign-in, so the database can finally
// tell a signed-in nurse from a stranger.
//
// Each call keeps the old direct path as a fallback, taken only when this
// browser holds no token: a database without staff sessions yet, or
// a database where the migrations have not been applied. That is what lets the
// deploy and the migrations land in either order without a broken clinic
// morning. It stops working of its own accord the moment Phase 3 revokes anon
// on the table — it is not a security control, and nothing here relies on it
// as one. Delete it once Phase 3 is in.

type RpcResult<T> = { data: T; error: { code?: string; message: string } | null };

const rpc = async <T>(fn: string, args: Record<string, unknown>): Promise<T> => {
  const { data, error } = await (supabase.rpc as unknown as (
    name: string,
    params: Record<string, unknown>
  ) => Promise<RpcResult<T>>)(fn, args);

  if (error) {
    if (isInvalidSessionError(error)) {
      // The token is dead: expired, logged out elsewhere, password changed, or
      // the account deactivated. Drop it rather than leave every later query
      // failing in a way the screens read as "case records unavailable" and
      // quietly answer with preview data.
      handleInvalidSession();
    }
    throw error;
  }

  return data;
};

/** PostgREST's own shape when `.single()` finds nothing; callers already handle it. */
const notFoundError = () =>
  Object.assign(new Error('JSON object requested, multiple (or no) rows returned'), {
    code: 'PGRST116',
  });

export const getReferralConversionStatus = (
  status: PatientCaseStatus | string
): PatientCaseConversionStatus => {
  if (['treatment_accepted', 'treatment_ongoing', 'completed'].includes(status)) {
    return 'converted';
  }

  if (['patient_did_not_proceed', 'non_compliant'].includes(status)) {
    return 'not_converted';
  }

  if (['lost_inactive', 'no_show'].includes(status)) {
    return 'inactive';
  }

  return 'pending';
};

const isReferralLoopCompleteStatus = (status: PatientCaseStatus | string) =>
  status === 'referred_back' || status === 'completed';

export const normalizePatientCaseStatus = (
  status: string
): Exclude<PatientCaseStatus, 'completed'> => {
  const normalizedStatus = status.toLowerCase().trim().replace(/[\s-]+/g, '_');

  if (normalizedStatus === 'awaiting_review') {
    return 'new_referral';
  }

  if (normalizedStatus === 'ongoing_treatment') {
    return 'treatment_ongoing';
  }

  // Completed and Referred Back are one status; `closed` is the older name for
  // the same thing.
  if (normalizedStatus === 'completed' || normalizedStatus === 'closed') {
    return 'referred_back';
  }

  if (
    normalizedStatus === 'referred_other_specialty' ||
    normalizedStatus === 'referred_to_other_speciality'
  ) {
    return 'referred_to_other_specialty';
  }

  if ((PATIENT_CASE_STATUS_OPTIONS as readonly string[]).includes(normalizedStatus)) {
    return normalizedStatus as Exclude<PatientCaseStatus, 'completed'>;
  }

  return 'new_referral';
};

const normalizePatientCasePayload = (
  payload: PatientCaseInsert | PatientCaseUpdate
): PatientCaseInsert | PatientCaseUpdate => {
  const normalized = { ...payload };
  const hasStatus = Object.prototype.hasOwnProperty.call(normalized, 'status');
  const hasExplicitReferredBackFlag = Object.prototype.hasOwnProperty.call(
    normalized,
    'referred_back_flag'
  );
  const hasExplicitReferredBackDate = Object.prototype.hasOwnProperty.call(
    normalized,
    'referred_back_date'
  );

  if (hasStatus && typeof normalized.status === 'string') {
    normalized.status = normalizePatientCaseStatus(normalized.status);
  }

  // Derive the flag for inserts. updatePatientCase strips this derived value
  // when the caller omitted both referred-back fields, preserving independent
  // flag updates made through the dedicated Referred-back column action.
  if (hasStatus && normalized.status && !hasExplicitReferredBackFlag) {
    normalized.referred_back_flag = isReferralLoopCompleteStatus(normalized.status);
  }

  if (
    normalized.referred_back_flag === true &&
    !normalized.referred_back_date &&
    !hasExplicitReferredBackDate
  ) {
    normalized.referred_back_date = formatDateToString(new Date());
  }

  if (normalized.referred_back_flag === false) {
    normalized.referred_back_date = null;
  }

  if (hasStatus && normalized.status) {
    const conversionStatus = getReferralConversionStatus(normalized.status);
    const now = new Date().toISOString();
    const today = formatDateToString(new Date());

    normalized.conversion_status = conversionStatus;
    normalized.status_changed_at = now;

    if (conversionStatus === 'converted' && !normalized.conversion_date) {
      normalized.conversion_date = today;
    }

    if (['treatment_accepted', 'treatment_ongoing'].includes(normalized.status)) {
      normalized.treatment_accepted_at = normalized.treatment_accepted_at ?? now;
    }

    // `referred_back` (the merged Completed / Referred Back status) closes the
    // referral loop. The external referral portal (get_external_referral_cases
    // RPC) dates a closed case by completed_at first, so it must be stamped too;
    // otherwise the case is dated by a weaker fallback and can age out of the
    // referring dentist's portal early.
    if (isReferralLoopCompleteStatus(normalized.status)) {
      normalized.completed_at = normalized.completed_at ?? now;
    }

    if (['patient_did_not_proceed', 'lost_inactive', 'no_show', 'non_compliant'].includes(normalized.status)) {
      normalized.inactive_at = normalized.inactive_at ?? now;
    }
  }

  return normalized;
};

const getStaffNameMap = async (staffIds: string[]): Promise<Record<string, string>> => {
  const uniqueStaffIds = [...new Set(staffIds.filter(Boolean))];

  if (uniqueStaffIds.length === 0) {
    return {};
  }

  const { data, error } = await supabase
    .from('staff')
    .select('id, name')
    .in('id', uniqueStaffIds);

  if (error) {
    throw error;
  }

  return (data ?? []).reduce<Record<string, string>>((accumulator, staffMember) => {
    accumulator[staffMember.id] = staffMember.name;
    return accumulator;
  }, {});
};

const getPatientCaseRow = async (caseId: string): Promise<PatientCase> => {
  const sessionToken = getStaffSessionToken();

  if (sessionToken) {
    const row = await rpc<PatientCase | null>('get_patient_case_row_by_session', {
      p_session_token: sessionToken,
      p_case_id: caseId,
    });

    if (!row) {
      throw notFoundError();
    }

    return row;
  }

  const { data, error } = await supabase
    .from('patient_cases')
    .select('*')
    .eq('id', caseId)
    .single();

  if (error) {
    throw directPathError(error);
  }

  return data;
};

// PostgREST silently caps any single response at 1000 rows. After the
// 2026-07-22 Plato backfill the case table exceeds that (1,000+ cases), so an
// unpaginated read drops the oldest cases off the referral list without any
// error. Page through with .range() until a short page signals the end. The
// filter chain is rebuilt per page because a Supabase query builder can only
// be awaited once.
const PATIENT_CASE_PAGE = 1000;

export const getPatientCases = async (
  filters: PatientCaseFilters = {}
): Promise<PatientCaseWithDetails[]> => {
  const sessionToken = getStaffSessionToken();

  if (sessionToken) {
    // No .range() loop here: the function returns the whole result as one JSON
    // array, so there is no 1,000-row cap to page around. Filters are bound
    // arguments rather than a hand-built query string — the old form had to
    // escape commas out of the search term before splicing it into an .or().
    return await rpc<PatientCaseWithDetails[]>('get_patient_cases_by_session', {
      p_session_token: sessionToken,
      p_clinic_id: filters.clinicId ?? null,
      p_status: filters.status && filters.status !== 'all' ? filters.status : null,
      p_referring_doctor: filters.referringDoctor ?? null,
      p_referring_clinic: filters.referringClinic ?? null,
      p_assigned_owner_staff_id: filters.assignedOwnerStaffId ?? null,
      p_search: filters.search?.trim() ? filters.search.trim() : null,
      p_include_closed: Boolean(filters.includeClosed),
    });
  }

  const buildQuery = () => {
    let query = supabase
      .from('patient_cases')
      .select(patientCaseSelect)
      .order('created_at', { ascending: false });

    if (filters.clinicId) {
      query = query.eq('clinic_id', filters.clinicId);
    }

    if (filters.status && filters.status !== 'all') {
      // Old `completed` rows are Referred Back, whether or not the backfill
      // migration has run yet, so the filter must find both.
      query =
        filters.status === 'referred_back'
          ? query.in('status', ['referred_back', 'completed'])
          : query.eq('status', filters.status);
    }

    if (!filters.includeClosed) {
      // `completed` is deliberately not excluded: it is Referred Back, and
      // Referred Back has always stayed in the default list.
      query = query.not('status', 'in', '("patient_did_not_proceed","lost_inactive","non_compliant")');
    }

    if (filters.referringDoctor) {
      query = query.eq('referring_doctor', filters.referringDoctor);
    }

    if (filters.referringClinic) {
      query = query.eq('referring_clinic', filters.referringClinic);
    }

    if (filters.assignedOwnerStaffId) {
      query = query.eq('assigned_owner_staff_id', filters.assignedOwnerStaffId);
    }

    if (filters.search?.trim()) {
      const escapedSearch = filters.search.trim().replace(/,/g, '\\,');
      query = query.or(
        `patient_display_name.ilike.%${escapedSearch}%,plato_reference_id.ilike.%${escapedSearch}%`
      );
    }

    return query;
  };

  const rows: unknown[] = [];
  for (let from = 0; ; from += PATIENT_CASE_PAGE) {
    const { data, error } = await buildQuery().range(from, from + PATIENT_CASE_PAGE - 1);

    if (error) {
      throw directPathError(error);
    }

    const page = data ?? [];
    rows.push(...page);

    if (page.length < PATIENT_CASE_PAGE) {
      break;
    }
  }

  return rows as PatientCaseWithDetails[];
};

/**
 * Doctor-private "My Patients" feed.
 *
 * Returns only the cases where the given staff member is the assigned owner.
 * If the schema ever grows a `treating_doctors` join (or similar), extend the
 * filter here — the route layer doesn't need to change.
 *
 * Resolves Twin City fixes-backlog item #23 (Dr Ang, 2026-04-09).
 */
export const getMyPatientCases = async (
  staffId: string
): Promise<PatientCaseWithDetails[]> => {
  if (!staffId) {
    return [];
  }

  const sessionToken = getStaffSessionToken();

  if (sessionToken) {
    // The staff id is now resolved from the session server-side; passing it
    // only means anything for an admin looking at someone else's feed. The old
    // call trusted whatever staffId the browser sent, so anyone holding the
    // public key could read any doctor's patient list by substituting an id.
    return await rpc<PatientCaseWithDetails[]>('get_my_patient_cases_by_session', {
      p_session_token: sessionToken,
      p_staff_id: staffId,
    });
  }

  const { data, error } = await supabase
    .from('patient_cases')
    .select(patientCaseSelect)
    .eq('assigned_owner_staff_id', staffId)
    .order('status_changed_at', { ascending: false });

  if (error) {
    throw directPathError(error);
  }

  return (data ?? []) as unknown as PatientCaseWithDetails[];
};

export const getPatientCase = async (caseId: string): Promise<PatientCaseWithDetails> => {
  const sessionToken = getStaffSessionToken();

  if (sessionToken) {
    const row = await rpc<PatientCaseWithDetails | null>('get_patient_case_by_session', {
      p_session_token: sessionToken,
      p_case_id: caseId,
    });

    if (!row) {
      throw notFoundError();
    }

    return row;
  }

  const { data, error } = await supabase
    .from('patient_cases')
    .select(patientCaseSelect)
    .eq('id', caseId)
    .single();

  if (error) {
    throw directPathError(error);
  }

  return data as unknown as PatientCaseWithDetails;
};

export const createPatientCase = async (
  patientCase: PatientCaseInsert
): Promise<PatientCaseWithDetails> => {
  const payload = normalizePatientCasePayload(patientCase) as PatientCaseInsert;
  const sessionToken = getStaffSessionToken();

  const data = sessionToken
    ? await rpc<PatientCaseWithDetails>('create_patient_case_by_session', {
        p_session_token: sessionToken,
        p_payload: payload,
      })
    : await (async () => {
        const { data: inserted, error } = await supabase
          .from('patient_cases')
          .insert(payload)
          .select(patientCaseSelect)
          .single();

        if (error) {
          throw directPathError(error);
        }

        return inserted;
      })();

  await addCaseActivity({
    case_id: data.id,
    activity_type: 'note',
    note: 'Case created',
    created_by: payload.created_by,
  });

  return data as unknown as PatientCaseWithDetails;
};

export const deletePatientCase = async (caseId: string): Promise<void> => {
  // Child rows (activities, attachments, referred-back notifications) cascade
  // via FK; referral_audit_logs.case_id is set null. So a single delete is
  // enough to remove the case from every list.
  const sessionToken = getStaffSessionToken();

  if (sessionToken) {
    await rpc<null>('delete_patient_case_by_session', {
      p_session_token: sessionToken,
      p_case_id: caseId,
    });
    return;
  }

  const { error } = await supabase
    .from('patient_cases')
    .delete()
    .eq('id', caseId);

  if (error) {
    throw directPathError(error);
  }
};

export const updatePatientCase = async (
  caseId: string,
  updates: PatientCaseUpdate,
  currentUserId: string
): Promise<PatientCaseWithDetails> => {
  const existingCase = await getPatientCaseRow(caseId);
  const normalizedUpdates = normalizePatientCasePayload(updates) as PatientCaseUpdate;

  if (!Object.prototype.hasOwnProperty.call(updates, 'conversion_status')) {
    delete normalizedUpdates.conversion_status;
  }

  if (!Object.prototype.hasOwnProperty.call(updates, 'conversion_date')) {
    delete normalizedUpdates.conversion_date;
  }

  if (
    !Object.prototype.hasOwnProperty.call(updates, 'referred_back_flag') &&
    !Object.prototype.hasOwnProperty.call(updates, 'referred_back_date')
  ) {
    delete normalizedUpdates.referred_back_flag;
    delete normalizedUpdates.referred_back_date;
  }

  // A key deleted from normalizedUpdates above means "leave this column alone",
  // and the function honours that the same way PostgREST did: only the keys
  // present in the payload are written, and a key present with a null value is
  // an explicit clear.
  const sessionToken = getStaffSessionToken();

  const data = sessionToken
    ? await rpc<PatientCaseWithDetails>('update_patient_case_by_session', {
        p_session_token: sessionToken,
        p_case_id: caseId,
        p_payload: normalizedUpdates,
      })
    : await (async () => {
        const { data: updated, error } = await supabase
          .from('patient_cases')
          .update(normalizedUpdates)
          .eq('id', caseId)
          .select(patientCaseSelect)
          .single();

        if (error) {
          throw directPathError(error);
        }

        return updated;
      })();

  const activitiesToCreate: PatientCaseActivityInsert[] = [];

  if (
    Object.prototype.hasOwnProperty.call(normalizedUpdates, 'status') &&
    normalizedUpdates.status &&
    normalizedUpdates.status !== existingCase.status
  ) {
    activitiesToCreate.push({
      case_id: caseId,
      activity_type: 'status_change',
      from_value: existingCase.status,
      to_value: normalizedUpdates.status,
      created_by: currentUserId,
    });
  }

  if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'assigned_owner_staff_id')) {
    const nextOwnerId = normalizedUpdates.assigned_owner_staff_id ?? null;
    if (nextOwnerId !== existingCase.assigned_owner_staff_id) {
      const nameMap = await getStaffNameMap(
        [existingCase.assigned_owner_staff_id ?? '', nextOwnerId ?? ''].filter(Boolean)
      );

      activitiesToCreate.push({
        case_id: caseId,
        activity_type: 'ownership_change',
        from_value: existingCase.assigned_owner_staff_id
          ? nameMap[existingCase.assigned_owner_staff_id] ?? 'Assigned'
          : 'Unassigned',
        to_value: nextOwnerId ? nameMap[nextOwnerId] ?? 'Assigned' : 'Unassigned',
        created_by: currentUserId,
      });
    }
  }

  if (
    Object.prototype.hasOwnProperty.call(normalizedUpdates, 'referred_back_flag') ||
    Object.prototype.hasOwnProperty.call(normalizedUpdates, 'referred_back_date')
  ) {
    const nextReferredBackFlag = normalizedUpdates.referred_back_flag ?? existingCase.referred_back_flag;
    const nextReferredBackDate = normalizedUpdates.referred_back_date ?? existingCase.referred_back_date;

    if (
      nextReferredBackFlag !== existingCase.referred_back_flag ||
      nextReferredBackDate !== existingCase.referred_back_date
    ) {
      activitiesToCreate.push({
        case_id: caseId,
        activity_type: 'referred_back',
        from_value: existingCase.referred_back_flag ? 'Yes' : 'No',
        to_value: nextReferredBackFlag ? 'Yes' : 'No',
        note: nextReferredBackDate ? `Referred back date: ${nextReferredBackDate}` : null,
        created_by: currentUserId,
      });
    }
  }

  if (activitiesToCreate.length > 0) {
    if (sessionToken) {
      await rpc<PatientCaseActivityWithDetails[]>('add_case_activities_by_session', {
        p_session_token: sessionToken,
        p_payload: activitiesToCreate,
      });
    } else {
      const { error: activityError } = await supabase
        .from('patient_case_activities')
        .insert(activitiesToCreate);

      if (activityError) {
        throw directPathError(activityError);
      }
    }
  }

  return data as unknown as PatientCaseWithDetails;
};

export interface SetReferredBackFlagResult {
  patientCase: PatientCaseWithDetails;
  previousReferredBackFlag: boolean;
  transitionedToReferredBack: boolean;
}

/**
 * Flip the referred-back flag on a case. Used by the dedicated column action
 * on the patient list/detail view. Returns whether this update transitioned
 * the flag from false -> true so the caller can fire the notification edge
 * function in a best-effort way after a successful flip.
 */
export const setReferredBackFlag = async (
  caseId: string,
  nextFlag: boolean,
  currentUserId: string,
  options: { referredBackDate?: string | null } = {}
): Promise<SetReferredBackFlagResult> => {
  const existingCase = await getPatientCaseRow(caseId);
  const previousFlag = existingCase.referred_back_flag;

  const updates: PatientCaseUpdate = {
    referred_back_flag: nextFlag,
  };

  if (nextFlag) {
    updates.referred_back_date =
      options.referredBackDate ??
      existingCase.referred_back_date ??
      formatDateToString(new Date());
  } else {
    updates.referred_back_date = null;
  }

  const patientCase = await updatePatientCase(caseId, updates, currentUserId);

  return {
    patientCase,
    previousReferredBackFlag: previousFlag,
    transitionedToReferredBack: !previousFlag && nextFlag,
  };
};

/**
 * Fire-and-forget call to the referred-back notification edge function. The
 * edge function itself is idempotent (24-hour dedup) and always returns 200
 * even if the email provider fails, so the UI should never block on this.
 */
export const triggerReferredBackNotification = async (caseId: string): Promise<void> => {
  try {
    const { error } = await supabase.functions.invoke('referred-back-notify', {
      body: { caseId },
    });

    if (error) {
      console.warn('referred-back-notify edge function returned an error:', error.message);
    }
  } catch (error) {
    // Best-effort — never throw to the caller.
    console.warn('referred-back-notify edge function invocation failed:', error);
  }
};

export const closePatientCase = async (
  caseId: string,
  currentUserId: string
): Promise<PatientCaseWithDetails> => {
  return updatePatientCase(
    caseId,
    {
      status: 'referred_back',
      next_action: null,
      next_action_due_date: null,
    },
    currentUserId
  );
};

export const addCaseActivity = async (
  activity: PatientCaseActivityInsert
): Promise<PatientCaseActivityWithDetails> => {
  const sessionToken = getStaffSessionToken();

  if (sessionToken) {
    const [inserted] = await rpc<PatientCaseActivityWithDetails[]>('add_case_activities_by_session', {
      p_session_token: sessionToken,
      p_payload: activity,
    });
    return inserted;
  }

  const { data, error } = await supabase
    .from('patient_case_activities')
    .insert(activity)
    .select(patientCaseActivitySelect)
    .single();

  if (error) {
    throw directPathError(error);
  }

  return data as unknown as PatientCaseActivityWithDetails;
};

export const listCaseActivities = async (
  caseId: string
): Promise<PatientCaseActivityWithDetails[]> => {
  const sessionToken = getStaffSessionToken();

  if (sessionToken) {
    return await rpc<PatientCaseActivityWithDetails[]>('list_case_activities_by_session', {
      p_session_token: sessionToken,
      p_case_id: caseId,
    });
  }

  const { data, error } = await supabase
    .from('patient_case_activities')
    .select(patientCaseActivitySelect)
    .eq('case_id', caseId)
    .order('created_at', { ascending: false });

  if (error) {
    throw directPathError(error);
  }

  return (data ?? []) as unknown as PatientCaseActivityWithDetails[];
};
